import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Send, X, Loader2, RefreshCw, FileText, Bot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage } from "@/lib/api";

const SESSION_KEY = "sofi_assistant_session";

const MD = {
  p: (p) => <p className="my-1.5 leading-relaxed" {...p} />,
  ul: (p) => <ul className="my-1.5 list-disc space-y-0.5 pl-4" {...p} />,
  ol: (p) => <ol className="my-1.5 list-decimal space-y-0.5 pl-4" {...p} />,
  li: (p) => <li className="pl-0.5" {...p} />,
  strong: (p) => <strong className="font-semibold" {...p} />,
  a: (p) => <a className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
  h1: (p) => <h3 className="mb-1 mt-2 font-heading text-sm font-bold" {...p} />,
  h2: (p) => <h3 className="mb-1 mt-2 font-heading text-sm font-bold" {...p} />,
  h3: (p) => <h4 className="mb-0.5 mt-2 font-heading text-sm font-semibold" {...p} />,
  code: (p) => <code className="rounded bg-background/60 px-1 py-0.5 text-[0.85em]" {...p} />,
};

const SUGGESTIONS = [
  "¿Cómo creo una nueva oportunidad en el Pipeline?",
  "¿Cómo solicito la factura a un proveedor?",
  "¿Cómo pido mis vacaciones en el portal del empleado?",
];

export const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]); // {role, content, sources?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, loading, open]);

  useEffect(() => {
    if (open && sessionId && msgs.length === 0) {
      api.get(`/ayuda/assistant/history/${sessionId}`)
        .then((r) => setMsgs(r.data?.messages || []))
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ayuda/assistant/chat", { message: q, session_id: sessionId });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMsgs((m) => [...m, { role: "assistant", content: data.answer, sources: data.sources || [] }]);
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setMsgs((m) => [...m, { role: "assistant", content: "Lo siento, ha ocurrido un error. Inténtalo de nuevo.", sources: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMsgs([]); setSessionId(null); localStorage.removeItem(SESSION_KEY);
  };

  const openSource = (s) => {
    setOpen(false);
    if (s.kind === "rrhh") navigate("/app/rrhh?view=informacion");
    else navigate(`/app/ayuda?article=${s.id}`);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Launcher — icono circular compacto (sin texto para no ocupar pantalla) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-launcher"
          className="group fixed bottom-24 right-5 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform duration-200 hover:scale-105 active:scale-95"
          title="Asistente IA"
          aria-label="Abrir asistente IA"
        >
          <Sparkles className="h-5 w-5" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-20 right-5 z-50 flex h-[min(600px,calc(100vh-7rem))] w-[min(400px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
          style={{ animation: "assist-in .18s ease-out" }}
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 border-b border-border bg-primary px-4 py-3 text-primary-foreground">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-foreground/15">
              <Bot className="h-4.5 w-4.5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold leading-tight">Asistente IA</p>
              <p className="text-[11px] leading-tight opacity-80">Cómo usar SOFI y normas del equipo</p>
            </div>
            <button onClick={reset} title="Nueva conversación" data-testid="assistant-reset"
              className="rounded-md p-1.5 transition-colors hover:bg-primary-foreground/15"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={() => setOpen(false)} title="Cerrar" data-testid="assistant-close"
              className="rounded-md p-1.5 transition-colors hover:bg-primary-foreground/15"><X className="h-4 w-4" /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto scroll-slim px-3.5 py-4">
            {msgs.length === 0 && !loading && (
              <div className="space-y-3" data-testid="assistant-welcome">
                <div className="rounded-xl bg-muted px-3.5 py-3 text-sm text-foreground/90">
                  ¡Hola! Soy tu asistente de SOFI. Respondo tanto sobre cómo usar la plataforma como sobre las normas del equipo (vacaciones, teletrabajo, ausencias…) según los documentos del portal del empleado. ¿En qué te ayudo?
                </div>
                <div className="space-y-1.5">
                  <p className="px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Prueba a preguntar</p>
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`assistant-suggestion-${i}`}
                      className="block w-full rounded-lg border border-border px-3 py-2 text-left text-xs text-foreground/80 transition-colors hover:border-primary/40 hover:bg-muted">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`assistant-msg-${m.role}`}
                  className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm ${
                    m.role === "user"
                      ? "rounded-br-sm bg-primary text-primary-foreground"
                      : "rounded-bl-sm bg-muted text-foreground/90"
                  }`}
                >
                  {m.role === "assistant"
                    ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{m.content}</ReactMarkdown>
                    : <span className="whitespace-pre-wrap">{m.content}</span>}
                  {m.role === "assistant" && m.sources?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 border-t border-border/60 pt-2">
                      {m.sources.map((s) => (
                        <button key={s.id} onClick={() => openSource(s)} data-testid={`assistant-source-${s.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground ring-1 ring-border transition-colors hover:text-primary hover:ring-primary/40">
                          <FileText className="h-3 w-3" />{s.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start" data-testid="assistant-loading">
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-muted px-3.5 py-2.5 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Pensando…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border p-2.5">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Escribe tu pregunta…"
                rows={1}
                data-testid="assistant-input"
                className="max-h-28 min-h-[40px] resize-none text-sm"
              />
              <Button size="icon" onClick={() => send()} disabled={loading || !input.trim()}
                data-testid="assistant-send" className="h-10 w-10 shrink-0">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-1.5 px-1 text-center text-[10px] text-muted-foreground">Respuestas basadas en los manuales y el portal del empleado de SOFI.</p>
          </div>
        </div>
      )}
      <style>{`@keyframes assist-in{from{opacity:0;transform:translateY(12px) scale(.98)}to{opacity:1;transform:none}}`}</style>
    </>
  );
};

export default AssistantWidget;
